<template>
  <div class="mt-6 space-y-6">
    <!-- Filters -->
    <div
      class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg px-5 py-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="lg:w-80 lg:flex-shrink-0">
        <div class="flex items-center gap-2">
          <ChartBarIcon class="h-5 w-5 text-teal-500" />
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">To hire or not to hire</h3>
        </div>
        <p class="mt-1 max-w-xs text-sm text-gray-500 dark:text-gray-400">
          Verwachte capaciteit en vraag per team en functieniveau voor de komende
          {{ horizonMonths }} maanden.
        </p>
      </div>

      <div class="ml-auto flex flex-wrap items-end gap-3 lg:flex-nowrap">
        <button
          v-if="hasActiveFilters"
          type="button"
          class="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          title="Zet alle dashboardfilters terug"
          @click="resetFilters"
        >
          <ArrowPathIcon class="h-4 w-4" aria-hidden="true" />
          Filters resetten
        </button>

        <Listbox v-model="selectedTeamIds" multiple v-slot="{ open: teamOpen }">
          <div class="relative w-48">
            <label class="flex flex-col text-xs font-medium text-gray-500 dark:text-gray-400">
              Teams
              <ListboxButton
                :title="selectedTeamDisplayText"
                aria-label="Teams selecteren"
                class="relative mt-1 flex h-9 w-full cursor-pointer items-center rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 pr-9 text-left text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                :class="teamOpen ? 'rounded-b-none' : ''"
              >
                <span class="block truncate">{{ selectedTeamDisplayText }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>
            </label>
            <ListboxOptions
              :unmount="false"
              class="absolute z-50 mt-0 max-h-64 w-full overflow-y-auto rounded-b-lg border border-t-0 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 text-sm shadow-lg focus:outline-none"
            >
              <ListboxOption
                v-for="team in availableTeams"
                :key="team.team_id"
                :value="team.team_id"
                v-slot="{ active, selected }"
                as="template"
              >
                <li
                  :class="[
                    active
                      ? 'bg-teal-50 dark:bg-teal-900/50 text-teal-900 dark:text-teal-200'
                      : 'text-gray-700 dark:text-gray-200',
                    'flex cursor-pointer items-center gap-2 px-3 py-2 select-none'
                  ]"
                >
                  <span class="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                    <CheckIcon v-if="selected" class="h-4 w-4 text-teal-600 dark:text-teal-400" />
                  </span>
                  <span class="block truncate" :class="selected ? 'font-medium' : ''">
                    {{ team.team_name }}
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </div>
        </Listbox>

        <Listbox v-model="selectedFunctionLevels" multiple v-slot="{ open: functionOpen }">
          <div class="relative w-44">
            <label class="flex flex-col text-xs font-medium text-gray-500 dark:text-gray-400">
              Functieniveaus
              <ListboxButton
                :title="selectedFunctionDisplayText"
                aria-label="Functieniveaus selecteren"
                class="relative mt-1 flex h-9 w-full cursor-pointer items-center rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 pr-9 text-left text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                :class="functionOpen ? 'rounded-b-none' : ''"
              >
                <span class="block truncate">{{ selectedFunctionDisplayText }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>
            </label>
            <ListboxOptions
              :unmount="false"
              class="absolute z-50 mt-0 max-h-64 w-full overflow-y-auto rounded-b-lg border border-t-0 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 text-sm shadow-lg focus:outline-none"
            >
              <ListboxOption
                v-for="level in availableLevels"
                :key="level"
                :value="level"
                v-slot="{ active, selected }"
                as="template"
              >
                <li
                  :class="[
                    active
                      ? 'bg-teal-50 dark:bg-teal-900/50 text-teal-900 dark:text-teal-200'
                      : 'text-gray-700 dark:text-gray-200',
                    'flex cursor-pointer items-center gap-2 px-3 py-2 select-none'
                  ]"
                >
                  <span class="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                    <CheckIcon v-if="selected" class="h-4 w-4 text-teal-600 dark:text-teal-400" />
                  </span>
                  <span class="block truncate" :class="selected ? 'font-medium' : ''">
                    {{ level }}
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </div>
        </Listbox>

        <label class="flex flex-col text-xs font-medium text-gray-500 dark:text-gray-400">
          Horizon
          <select
            v-model.number="horizonMonths"
            class="mt-1 h-9 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200"
          >
            <option :value="6">6 maanden</option>
            <option :value="12">12 maanden</option>
            <option :value="24">24 maanden</option>
          </select>
        </label>
      </div>
    </div>

    <!-- Loading / error -->
    <div v-if="isLoading" class="py-12 text-center">
      <div
        class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-teal-600 mx-auto"
      ></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Prognose laden...</p>
    </div>

    <div v-else-if="error" class="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
      Prognose kon niet worden geladen.
      <button class="ml-2 text-teal-600 hover:underline" @click="refetch()">
        Opnieuw proberen
      </button>
    </div>

    <template v-else-if="forecast">
      <!-- Summary cards -->
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 class="text-base font-medium text-gray-900 dark:text-white">Maandoverzicht</h4>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Kies de maand waarvoor de capaciteitscijfers worden getoond.
          </p>
        </div>
        <label class="flex flex-col text-xs font-medium text-gray-500 dark:text-gray-400">
          Maand
          <select
            v-model="selectedSummaryMonth"
            aria-label="Maand voor samenvatting"
            class="mt-1 h-9 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200"
          >
            <option v-for="month in months" :key="month" :value="month">
              {{ formatMonth(month) }}
            </option>
          </select>
        </label>
      </div>

      <div class="grid grid-cols-2 items-stretch gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <div class="flex h-full flex-col rounded-lg bg-white px-5 py-4 shadow dark:bg-gray-800">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Totale FTE</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {{ formatFte(aggregatedSeries.total_fte[selectedSummaryMonth]) }} FTE
          </p>
          <p class="mt-auto pt-2 text-xs text-gray-400">{{ referenceLabel }}</p>
        </div>
        <div class="flex h-full flex-col rounded-lg bg-white px-5 py-4 shadow dark:bg-gray-800">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Delta capaciteit</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {{ formatFte(referenceCapacityBreakdown.delta) }} FTE
          </p>
          <div
            class="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400"
          >
            <div class="flex justify-between gap-2">
              <span>FB-tijd</span>
              <span class="font-medium text-gray-700 dark:text-gray-200">
                {{ formatFte(referenceCapacityBreakdown.fbTime) }}
              </span>
            </div>
            <div class="flex justify-between gap-2">
              <span>Verzuim</span>
              <span class="font-medium text-gray-700 dark:text-gray-200">
                {{ formatFte(referenceCapacityBreakdown.verzuim) }}
              </span>
            </div>
            <div class="flex justify-between gap-2">
              <span>Verlof</span>
              <span class="font-medium text-gray-700 dark:text-gray-200">
                {{ formatFte(referenceCapacityBreakdown.verlof) }}
              </span>
            </div>
            <div class="flex justify-between gap-2">
              <span>Overig</span>
              <span class="font-medium text-gray-700 dark:text-gray-200" title="PL / interne tijd">
                {{ formatFte(referenceCapacityBreakdown.overig) }}
              </span>
            </div>
          </div>
          <p class="mt-auto pt-2 text-xs text-gray-400">{{ referenceLabel }}</p>
        </div>
        <div class="flex h-full flex-col rounded-lg bg-white px-5 py-4 shadow dark:bg-gray-800">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Beschikbare capaciteit</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {{ formatFte(aggregatedSeries.supply_fte[selectedSummaryMonth]) }} FTE
          </p>
          <p class="mt-auto pt-2 text-xs text-gray-400">{{ referenceLabel }}</p>
        </div>
        <div class="flex h-full flex-col rounded-lg bg-white px-5 py-4 shadow dark:bg-gray-800">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Verwachte vraag</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {{ formatFte(maxExpectedDemand) }} FTE
          </p>
          <p class="mt-auto pt-2 text-xs text-gray-400">piek in de horizon</p>
        </div>
        <div
          v-if="hasBudgetTarget"
          class="flex h-full flex-col rounded-lg bg-white px-5 py-4 shadow dark:bg-gray-800"
        >
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">FTE-doelstelling</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {{ formatFte(totalBudgetTarget) }} FTE
          </p>
          <p class="mt-auto pt-2 text-xs text-gray-400">voor de geselecteerde filters</p>
        </div>
        <div class="flex h-full flex-col rounded-lg bg-white px-5 py-4 shadow dark:bg-gray-800">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Grootste tekort</p>
          <p
            class="mt-1 text-2xl font-semibold"
            :class="
              maxShortage > 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-emerald-600 dark:text-emerald-400'
            "
          >
            {{ formatFte(maxShortage) }} FTE
          </p>
          <p class="mt-auto pt-2 text-xs text-gray-400">{{ firstShortageLabel }}</p>
        </div>
      </div>

      <!-- Chart -->
      <div
        class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden"
      >
        <div
          class="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-gray-700"
        >
          <h4 class="text-base font-medium text-gray-900 dark:text-white">Capaciteit vs. vraag</h4>
          <div class="flex items-center gap-1.5">
            <span class="group relative inline-flex">
              <InformationCircleIcon
                class="h-4 w-4 cursor-help text-gray-400 transition-colors group-hover:text-teal-500 dark:group-hover:text-teal-400"
                aria-hidden="true"
              />
              <span
                class="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden w-80 rounded-lg bg-gray-800 px-3 py-2 text-left text-xs font-normal leading-5 text-white shadow-lg group-hover:block dark:bg-gray-900"
              >
                <strong>Simplicate:</strong> gebruikt de oorspronkelijke start- en einddatums en
                alleen ingevulde DPW&rsquo;s.
                <br />
                <strong>Aangepast:</strong> open kansen starten op zijn vroegst volgende maand,
                schuiven hun einddatum mee en gebruiken 6 maanden als de einddatum ontbreekt.
                Ontbrekende DPW wordt geschat met de mediane PM-uren van vergelijkbare historische
                projecten en hun historische team-/functiemix.
              </span>
            </span>
            <div
              class="inline-flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600"
              role="group"
              aria-label="THONTH-dashboardversie"
            >
              <button
                type="button"
                class="px-3 py-1.5 text-sm font-medium transition-colors"
                :class="
                  forecastMode === 'legacy'
                    ? 'bg-teal-500 text-white'
                    : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                "
                :aria-pressed="forecastMode === 'legacy'"
                aria-label="Simplicate THONTH"
                @click="forecastMode = 'legacy'"
              >
                Simplicate
              </button>
              <button
                type="button"
                class="px-3 py-1.5 text-sm font-medium transition-colors"
                :class="
                  forecastMode === 'realistic'
                    ? 'bg-teal-500 text-white'
                    : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                "
                :aria-pressed="forecastMode === 'realistic'"
                aria-label="Aangepaste THONTH"
                @click="forecastMode = 'realistic'"
              >
                Aangepast
              </button>
            </div>
          </div>
        </div>
        <WorkforceForecastChart
          :months="forecast.months"
          :series="aggregatedSeries"
          :range="scenarioRange"
          :transitions="visibleTransitions"
        />
      </div>

      <div
        class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden"
      >
        <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h4 class="text-base font-medium text-gray-900 dark:text-white">Bekende wijzigingen</h4>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Bekende instroom, uitstroom en functiewijzigingen binnen de forecast-horizon.
          </p>
        </div>

        <div
          v-if="knownChanges.length === 0"
          class="px-5 py-6 text-sm text-gray-500 dark:text-gray-400"
        >
          Geen bekende wijzigingen binnen deze forecast-horizon.
        </div>
        <ul v-else class="divide-y divide-gray-200 dark:divide-gray-700">
          <li
            v-for="(change, index) in knownChanges"
            :key="`${change.type}-${change.date}-${change.employee_name || 'unknown'}-${index}`"
            class="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:gap-4"
          >
            <time class="w-24 flex-shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ formatChangeDate(change.date) }}
            </time>
            <span
              class="inline-flex w-fit flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-medium"
              :class="knownChangeClasses[change.type] || knownChangeClasses.role_change"
            >
              {{ knownChangeLabels[change.type] || 'Wijziging' }}
            </span>
            <div class="min-w-0 text-sm text-gray-700 dark:text-gray-300">
              <span class="font-medium text-gray-900 dark:text-white">
                {{ change.employee_name || 'Onbekende medewerker' }}
              </span>
              <span v-if="change.team" class="text-gray-500 dark:text-gray-400">
                · {{ change.team }}
              </span>
              <span v-if="change.type === 'role_change'" class="ml-1">
                ({{ change.from_level }} → {{ change.to_level }})
              </span>
              <span v-else-if="change.type === 'departure' && change.function_level" class="ml-1">
                ({{ change.function_level }})
              </span>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import {
  ArrowPathIcon,
  ChartBarIcon,
  CheckIcon,
  InformationCircleIcon,
  ChevronUpDownIcon
} from '@heroicons/vue/24/outline'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { workforceForecastService } from '@/services/workforceForecastService.js'
import WorkforceForecastChart from './WorkforceForecastChart.vue'

const props = defineProps({
  reloadTrigger: { type: Number, default: 0 }
})

const selectedTeamIds = ref([])
const selectedFunctionLevels = ref([])
const horizonMonths = ref(12)
const forecastMode = ref('legacy')

const knownChangeLabels = {
  arrival: 'In dienst',
  departure: 'Uit dienst',
  role_change: 'Functiewijziging'
}

const knownChangeClasses = {
  arrival: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  departure: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  role_change: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
}

function createForecastQuery(scenario) {
  return {
    queryKey: computed(() => [
      'workforce-forecast',
      scenario,
      horizonMonths.value,
      forecastMode.value
    ]),
    queryFn: () =>
      workforceForecastService.getForecast({
        scenario,
        horizon_months: horizonMonths.value,
        forecast_mode: forecastMode.value
      }),
    staleTime: 1000 * 60 * 5
  }
}

const {
  data: forecast,
  isLoading: isBaseLoading,
  error: baseError,
  refetch: refetchBase
} = useQuery(createForecastQuery('base'))
const {
  data: lowForecast,
  isLoading: isLowLoading,
  error: lowError,
  refetch: refetchLow
} = useQuery(createForecastQuery('low'))
const {
  data: highForecast,
  isLoading: isHighLoading,
  error: highError,
  refetch: refetchHigh
} = useQuery(createForecastQuery('high'))

const isLoading = computed(() => isBaseLoading.value || isLowLoading.value || isHighLoading.value)
const error = computed(() => baseError.value || lowError.value || highError.value)

async function refetch() {
  await Promise.all([refetchBase(), refetchLow(), refetchHigh()])
}

watch(
  () => props.reloadTrigger,
  () => {
    void refetch()
  }
)

const groups = computed(() => forecast.value?.groups || [])
const lowGroups = computed(() => lowForecast.value?.groups || [])
const highGroups = computed(() => highForecast.value?.groups || [])
const months = computed(() => forecast.value?.months || [])
const roleTransitions = computed(() => forecast.value?.role_transitions || [])
const knownChanges = computed(() => forecast.value?.known_changes || [])
const referenceMonth = computed(() => forecast.value?.reference_month || months.value[0] || '')
const selectedSummaryMonth = ref('')

watch(
  months,
  availableMonths => {
    if (!availableMonths.includes(selectedSummaryMonth.value)) {
      selectedSummaryMonth.value = availableMonths[0] || ''
    }
  },
  { immediate: true }
)

const availableTeams = computed(() => {
  const map = new Map()
  for (const group of groups.value) map.set(group.team_id, group)
  return [...map.values()].sort((a, b) => a.team_name.localeCompare(b.team_name, 'nl'))
})

const availableLevels = computed(() =>
  [...new Set(groups.value.map(group => group.function_level))].sort((a, b) =>
    a.localeCompare(b, 'nl')
  )
)

const selectedTeamDisplayText = computed(() => {
  if (selectedTeamIds.value.length === 0) return 'Alle teams'
  if (selectedTeamIds.value.length === 1) {
    return (
      availableTeams.value.find(team => team.team_id === selectedTeamIds.value[0])?.team_name ||
      selectedTeamIds.value[0]
    )
  }
  return `${selectedTeamIds.value.length} teams geselecteerd`
})

const selectedFunctionDisplayText = computed(() => {
  if (selectedFunctionLevels.value.length === 0) return 'Alle functieniveaus'
  if (selectedFunctionLevels.value.length === 1) return selectedFunctionLevels.value[0]
  return `${selectedFunctionLevels.value.length} functieniveaus geselecteerd`
})

// Keep selections valid after a refresh, without making the two filters
// dependent on one another. This means a user can select any combination of
// teams and function levels and the result is the intersection of both lists.
watch(groups, () => {
  // Keep the current selection while a new scenario or horizon is loading.
  if (isLoading.value && groups.value.length === 0) return

  const teamIds = new Set(availableTeams.value.map(team => team.team_id))
  const levels = new Set(availableLevels.value)
  selectedTeamIds.value = selectedTeamIds.value.filter(teamId => teamIds.has(teamId))
  selectedFunctionLevels.value = selectedFunctionLevels.value.filter(level => levels.has(level))
})

const hasActiveFilters = computed(
  () =>
    selectedTeamIds.value.length > 0 ||
    selectedFunctionLevels.value.length > 0 ||
    horizonMonths.value !== 12 ||
    selectedSummaryMonth.value !== referenceMonth.value
)

function resetFilters() {
  selectedTeamIds.value = []
  selectedFunctionLevels.value = []
  horizonMonths.value = 12
  selectedSummaryMonth.value = referenceMonth.value
}

function filterVisibleGroups(sourceGroups) {
  return sourceGroups.filter(group => {
    if (selectedTeamIds.value.length > 0 && !selectedTeamIds.value.includes(group.team_id)) {
      return false
    }
    if (
      selectedFunctionLevels.value.length > 0 &&
      !selectedFunctionLevels.value.includes(group.function_level)
    ) {
      return false
    }
    return true
  })
}

const visibleGroups = computed(() => filterVisibleGroups(groups.value))
const visibleLowGroups = computed(() => filterVisibleGroups(lowGroups.value))
const visibleHighGroups = computed(() => filterVisibleGroups(highGroups.value))

const visibleTransitions = computed(() =>
  roleTransitions.value.filter(transition => {
    if (selectedTeamIds.value.length > 0 && !selectedTeamIds.value.includes(transition.team_id)) {
      return false
    }

    if (
      selectedFunctionLevels.value.length > 0 &&
      !selectedFunctionLevels.value.includes(transition.from_level) &&
      !selectedFunctionLevels.value.includes(transition.to_level)
    ) {
      return false
    }

    return true
  })
)

const SERIES_KEYS = [
  'supply_fte',
  'total_fte',
  'max_decl_fte',
  'fb_time_fte',
  'verzuim_fte',
  'verlof_fte',
  'overig_fte',
  'capacity_delta_fte',
  'committed_demand_fte',
  'historical_demand_fte',
  'current_expected_demand_fte',
  'gap_fte'
]

function aggregateSeries(sourceGroups) {
  const result = {}
  for (const key of SERIES_KEYS) {
    result[key] = {}
    for (const month of months.value) {
      result[key][month] = sourceGroups.reduce(
        (sum, group) => sum + (Number(group.series?.[key]?.[month]) || 0),
        0
      )
    }
  }
  return result
}

const aggregatedSeries = computed(() => aggregateSeries(visibleGroups.value))
const lowAggregatedSeries = computed(() => aggregateSeries(visibleLowGroups.value))
const highAggregatedSeries = computed(() => aggregateSeries(visibleHighGroups.value))
const scenarioRange = computed(() => ({
  low: lowAggregatedSeries.value.current_expected_demand_fte,
  high: highAggregatedSeries.value.current_expected_demand_fte
}))

function roundFte(value) {
  return Math.round((Number(value) || 0) * 100) / 100
}

const referenceCapacityBreakdown = computed(() => {
  const month = selectedSummaryMonth.value
  const series = aggregatedSeries.value
  const total = roundFte(series.total_fte[month])
  const supply = roundFte(series.supply_fte[month])
  // This is a supply-side metric and must not change when only the sales
  // opportunity date policy changes. Keep the fallback for older responses.
  const delta = roundFte(
    series.capacity_delta_fte?.[month] == null ? total - supply : series.capacity_delta_fte[month]
  )
  const fbTime = roundFte(series.fb_time_fte[month])
  const verzuim = roundFte(series.verzuim_fte[month])
  const verlof = roundFte(series.verlof_fte[month])

  return {
    delta,
    fbTime,
    verzuim,
    verlof,
    // Keep the displayed breakdown consistent with the displayed total and
    // available capacity after rounding to two decimals.
    overig: roundFte(delta - fbTime - verzuim - verlof)
  }
})

const hasBudgetTarget = computed(() =>
  visibleGroups.value.some(group => group.fte_budget_target != null)
)

const totalBudgetTarget = computed(() =>
  visibleGroups.value.reduce((sum, group) => sum + (Number(group.fte_budget_target) || 0), 0)
)

const maxExpectedDemand = computed(() =>
  months.value.reduce(
    (max, month) =>
      Math.max(max, Number(aggregatedSeries.value.current_expected_demand_fte[month]) || 0),
    0
  )
)

const maxShortage = computed(() =>
  months.value.reduce(
    (max, month) => Math.max(max, Number(aggregatedSeries.value.gap_fte[month]) || 0),
    0
  )
)

const firstShortageLabel = computed(() => {
  const month = months.value.find(month => Number(aggregatedSeries.value.gap_fte[month]) > 0)
  return month ? `vanaf ${formatMonth(month)}` : 'geen tekort verwacht'
})

const referenceLabel = computed(() => formatMonth(selectedSummaryMonth.value))
function formatFte(value) {
  return (Number(value) || 0).toFixed(2)
}

function formatChangeDate(value) {
  if (!value) return '—'
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`
}

function formatMonth(monthKey) {
  if (!monthKey) return '—'
  const [year, month] = monthKey.split('-')
  const monthNames = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december'
  ]
  return `${monthNames[parseInt(month, 10) - 1]} ${year}`
}
</script>
